import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import { GhostButton } from '@/components/ui-kit/Buttons'
import { Headline } from '@/components/ui-kit/Primitives'

const SLIDES = ['what', 'odds', 'skip'] as const

/**
 * Hilfe als Overlay, nicht als eigene Seite.
 *
 * Sie ist aus jeder Phase erreichbar und soll niemanden aus dem Ablauf werfen -
 * wer sie schließt, steht wieder genau dort, wo er war.
 */
export function HelpOverlay({ onClose }: { onClose: () => void }) {
    const { t } = useTranslation()
    const steps = t('help.example.steps', { returnObjects: true }) as string[]

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label={t('help.title')}
            className="fixed inset-0 z-200 wb-scroll flex flex-col overflow-y-auto bg-wb-bg/97 px-5 pt-[max(1.5rem,env(safe-area-inset-top))] pb-[max(1.5rem,env(safe-area-inset-bottom))]"
        >
            <div className="flex items-center justify-between">
                <Headline className="text-2xl">{t('help.title')}</Headline>
                <button
                    type="button"
                    onClick={onClose}
                    aria-label={t('common.close')}
                    className="flex size-9 items-center justify-center rounded-full border border-wb-border bg-wb-panel-hi text-lg"
                >
                    ×
                </button>
            </div>

            <p className="mt-2 text-sm text-wb-dim">{t('help.intro')}</p>

            <ul className="mt-4 flex flex-col gap-2">
                {SLIDES.map((slide) => (
                    <li
                        key={slide}
                        className="rounded-2xl border border-wb-border bg-wb-panel p-3.5"
                    >
                        <h3 className="text-sm font-extrabold">
                            {t(`help.slides.${slide}.title`)}
                        </h3>
                        <p className="mt-1 text-[13px] leading-snug text-wb-dim">
                            {t(`help.slides.${slide}.body`)}
                        </p>
                    </li>
                ))}
            </ul>

            <section className="mt-4 rounded-2xl border border-wb-accent/40 bg-wb-accent/5 p-4">
                <h3 className="text-sm font-extrabold text-wb-accent">{t('help.example.title')}</h3>
                <ol className="mt-2 flex flex-col gap-1.5">
                    {steps.map((step, index) => (
                        <li key={step} className="flex gap-2 text-[13px] text-wb-dim">
                            <span className="flex-none wb-num font-bold text-wb-muted">
                                {index + 1}
                            </span>
                            {step}
                        </li>
                    ))}
                </ol>
            </section>

            {/* mb-1: fester Mindestabstand zum Knopf. mt-auto am Knopf schrumpft auf
                null, sobald der Inhalt den Bildschirm fuellt - auf dem iPhone mit
                eingeblendeten Browserleisten klebten die Links dann am Knopf
                (WETTBANDIT-52). */}
            <footer className="mt-4 mb-1 flex justify-center gap-4 text-[11px] text-wb-muted">
                <Link to="/datenschutz" className="underline">
                    {t('legal.privacy.title')}
                </Link>
                <Link to="/impressum" className="underline">
                    {t('legal.imprint.title')}
                </Link>
            </footer>

            <GhostButton className="mt-auto" onClick={onClose}>
                {t('common.close')}
            </GhostButton>
        </div>
    )
}
